'use client';

import { useCallback, useContext, useState, FC } from 'react';
import { Dialog, Tabs, TabsItem } from '@/_components';
import type {
  EditUserInfoTab,
  UserEditDialogProps,
} from './UserEditDialog.types';
import { UserSettingsForm } from './_components/UserSettingsForm';
import { ConfirmChangingEntityEmailDialog } from './_components/ConfirmChangingEntityEmailDialog';
import { UserEditForm } from './_components/UserEditForm';
import { ConfirmActivateUserDialog } from './_components/ConfirmActivateUserDialog';
import { ConfirmDeactivateUserDialog } from './_components/ConfirmDeactivateUserDialog';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { updateUser } from '@/_api/users';
import { User } from '@/_types/models/user';

export const UserEditDialog: FC<UserEditDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    addOrUpdateUser,
    refreshCompanyDetails,
    setSelectedUser,
    selectedUser,
    selectedUserPrimaryCompanies,
  } = useContext(CompanyContext);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [newEmail, setChangingEmail] = useState<string | undefined>(undefined);
  const [active, setActive] = useState<EditUserInfoTab>('details');
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [activateUserConfirmationOpen, setActivateUserConfirmationOpen] =
    useState<boolean>(false);
  const [isLoading, setLoading] = useState(false);

  const onCloseChangingEmail = () => {
    onClose();
    setActive('details');
    setChangingEmail(undefined);
  };

  const onCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const tabItems: Array<TabsItem> = [
    {
      title: 'Details',
      onClick: () => setActive('details'),
      isActive: active === 'details',
    },
    {
      title: 'Settings',
      onClick: () => setActive('settings'),
      isActive: active === 'settings',
    },
  ];

  const refresh = useCallback(
    async (response: User) => {
      addOrUpdateUser(response);
      setSelectedUser(response);
      await refreshCompanyDetails();

      addToast('success', 'User updated successfully');
    },
    [addOrUpdateUser, setSelectedUser, refreshCompanyDetails, addToast],
  );

  const onToggleDeactivateAccount = useCallback(
    async (
      paying: boolean,
      isDeactivatingAllEntities?: boolean,
      isActivatingAllEntities?: boolean,
    ) => {
      if (!selectedUser) return;

      try {
        if (
          paying &&
          (selectedUser?.preferences?.has_temp_email || !selectedUser?.email)
        ) {
          return addToast(
            'error',
            'User email is required before activating account.',
          );
        }

        setLoading(true);

        const response = await updateUser({
          is_activating_all_entities: isActivatingAllEntities,
          is_deactivating_all_entities: isDeactivatingAllEntities,
          paying,
          userId: selectedUser.id!,
          token,
        });

        if (response?.error) {
          addToast('error', 'Error updating user', {
            error: response.error,
          });
        }

        await refresh(response);
      } catch (error) {
        addToast('error', 'Error updating user', { error });
      } finally {
        setLoading(false);
        onCloseConfirmation();
        setActivateUserConfirmationOpen(false);
      }
    },
    [addToast, refresh, selectedUser, token],
  );

  const onToggleActivateAccount = useCallback(
    async (isActivatingAllEntities?: boolean) => {
      return await onToggleDeactivateAccount(
        true,
        false,
        isActivatingAllEntities,
      );
    },
    [onToggleDeactivateAccount],
  );

  const activeTab: Record<EditUserInfoTab, React.ReactNode> = {
    details: (
      <UserEditForm onClose={onClose} setChangingEmail={setChangingEmail} />
    ),
    settings: (
      <UserSettingsForm
        isLoading={isLoading}
        onOpen={() => setOpenConfirmation(true)}
        onOpenActivateUser={() => setActivateUserConfirmationOpen(true)}
        onToggleDeactivateAccount={onToggleDeactivateAccount}
        refresh={refresh}
        setLoading={setLoading}
      />
    ),
  };

  return (
    <>
      <ConfirmChangingEntityEmailDialog
        newEmail={newEmail}
        onClose={onCloseChangingEmail}
      />
      <Dialog isOpen={isOpen} title="Edit user" onClose={onCloseChangingEmail}>
        <Dialog.Content>
          <Tabs items={tabItems} noPadding={true}>
            {activeTab[active]}
          </Tabs>
        </Dialog.Content>
      </Dialog>
      <ConfirmDeactivateUserDialog
        isOpen={openConfirmation}
        isLoading={isLoading}
        isPrimaryOwnerOfAtLeastOneCompany={
          selectedUserPrimaryCompanies.length > 0
        }
        onClose={onCloseConfirmation}
        onToggleDeactivateAccount={onToggleDeactivateAccount}
      ></ConfirmDeactivateUserDialog>
      <ConfirmActivateUserDialog
        companiesOfWhichIsPrimaryOwner={selectedUserPrimaryCompanies}
        isOpen={activateUserConfirmationOpen}
        isLoading={isLoading}
        onClose={() => setActivateUserConfirmationOpen(false)}
        onToggleActivateAccount={onToggleActivateAccount}
      ></ConfirmActivateUserDialog>
    </>
  );
};
